import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Mail, Lock, AlertCircle, Coffee, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { flag } from '../../config';
import lazyAccountPage from '../../components/site/auth/lazyAccountPage';

// SLOT_MODEL on: the redesigned page (site redesign Phase 6), loaded on demand.
const VenueLoginSite = lazyAccountPage(
  'VenueLoginSite',
  () => import(/* webpackChunkName: "account-pages" */ '../account/VenueLoginSite'),
);

/** /venue/login: the redesigned page with SLOT_MODEL on, the legacy V5 page (unchanged) off. */
export default function VenueLogin() {
  return flag('SLOT_MODEL') ? <VenueLoginSite /> : <VenueLoginLegacy />;
}

const VenueLoginLegacy = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user?.venue_partner_id) {
      navigate('/venue/dashboard');
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      // The dashboard's own guard redirects accounts that are not venue partners.
      navigate('/venue/dashboard');
    } else {
      let errorMessage = 'Login failed. Please try again.';
      if (typeof result.error === 'string') {
        errorMessage = result.error;
      } else if (typeof result.error === 'object') {
        errorMessage = JSON.stringify(result.error);
      }
      setError(errorMessage);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
      <div className="w-full max-w-md px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* V5 Card */}
          <div 
            className="bg-white p-8"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              boxShadow: '0 8px 16px rgba(30, 27, 75, 0.08)'
            }}
          >
            {/* Logo/Icon */}
            <div className="text-center mb-6">
              <div 
                className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)', borderRadius: '12px' }}
              >
                <Coffee size={32} style={{ color: '#14b8a6' }} />
              </div>
              <h1 
                className="text-[32px] mb-2"
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
              >
                Venue Partner Login
              </h1>
              <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                Access your coaster dashboard
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="px-4 py-3 mb-4 flex items-center gap-2"
                style={{ 
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  borderRadius: '12px',
                  color: '#ef4444'
                }}
              >
                <AlertCircle size={20} />
                <span className="text-[14px]">{error}</span>
              </motion.div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label 
                  className="block text-[14px] font-medium mb-2" 
                  style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                >
                  Email
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-4 top-1/2 transform -translate-y-1/2"
                    size={20}
                    style={{ color: 'rgba(30, 27, 75, 0.4)' }}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@venue.com"
                    required
                    className="w-full h-12 pl-12 pr-4 text-[16px] transition-all"
                    style={{
                      backgroundColor: '#FFFFFF',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      color: '#1e1b4b',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#14b8a6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(20, 184, 166, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              <div>
                <label 
                  className="block text-[14px] font-medium mb-2" 
                  style={{ color: 'rgba(30, 27, 75, 0.7)' }}
                >
                  Password
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-4 top-1/2 transform -translate-y-1/2"
                    size={20}
                    style={{ color: 'rgba(30, 27, 75, 0.4)' }}
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full h-12 pl-12 pr-12 text-[16px] transition-all"
                    style={{
                      backgroundColor: '#FFFFFF',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      color: '#1e1b4b',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#14b8a6';
                      e.target.style.boxShadow = '0 0 0 4px rgba(20, 184, 166, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(30, 27, 75, 0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors"
                    style={{ color: 'rgba(30, 27, 75, 0.4)' }}
                  >
                    {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 flex items-center justify-center gap-2 text-[14px] font-semibold text-white transition-all"
                style={{
                  background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(20, 184, 166, 0.4), 0 0 30px rgba(20, 184, 166, 0.2)',
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Logging in...
                  </>
                ) : (
                  <>
                    <LogIn size={20} />
                    Log In
                  </>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-6 text-center">
              <p className="text-[14px] mb-3">
                <button type="button" onClick={() => navigate('/forgot-password')} className="font-semibold hover:underline" style={{ color: '#14b8a6' }}>Forgot password?</button>
              </p>
              <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                Not a venue partner yet?{' '}
                <button
                  onClick={() => navigate('/for-venues')}
                  className="font-semibold hover:underline transition-colors"
                  style={{ color: '#14b8a6' }}
                >
                  Apply here
                </button>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

